import { apiFetch } from '../../../shared/api/apiFetch'

export interface TeamMapPinDto {
  id: string
  name: string | null
  type: string | null
  latitude: number
  longitude: number
  /** Only a `custom` pin carries these: the glyph the user picked, and the
      pin's colour as the app's packed ARGB int. */
  customSymbol?: string | null
  color?: number | null
  // Bare picture ids (UUIDs) shared with the mobile app; the bytes live in
  // Cloud Storage at `mapPinsImages/{id}.png`. Always present on the wire (the
  // API returns [] for a pin with no pictures).
  images: string[]
  /** The pin's free-text note, shared with the app. Absent or null when the
      pin has none. */
  description?: string | null
  /** The folder of the team's pin collection this pin is filed in; null at the
      root. The create/update bodies below never carry it — a merge write with
      no folderId key leaves the stored value alone, so those two calls cannot
      express "file" or "unfile" either way. CLUB-676: the editor's Save files
      or unfiles a pin instead through {@link fileTeamMapPin}, a single-field
      endpoint of its own. */
  folderId?: string | null
  /** The pin's position inside its folder, as the app orders it. */
  orderIndex?: number | null
}

interface ListTeamMapPinsResponse {
  items: TeamMapPinDto[]
}

// Collection root keeps the trailing slash because the .NET endpoints register
// the list/create routes as `MapGet("/")` / `MapPost("/")` inside a route group
// (`/clubhouse/teams/{teamId}/map-pins`), and the integration tests in
// `apps/wehunt-web-api/WeHunt.Api.Tests/Modules/TeamDocuments/Api/TeamMapPinsEndpointsTests.cs`
// hit `/map-pins/` verbatim. Sub-paths (`/{pinId}`) are concatenated without an
// extra slash.
const base = (teamId: string) => `/clubhouse/teams/${encodeURIComponent(teamId)}/map-pins`

export async function listTeamMapPins(teamId: string, token: string): Promise<TeamMapPinDto[]> {
  const r = await apiFetch<ListTeamMapPinsResponse>(`${base(teamId)}/`, { token })
  return r.items
}

export async function createTeamMapPin(
  teamId: string,
  body: {
    name: string | null
    type: string | null
    latitude: number
    longitude: number
    images?: string[]
    customSymbol?: string
    color?: number
    description?: string
  },
  token: string,
): Promise<TeamMapPinDto> {
  return apiFetch<TeamMapPinDto>(`${base(teamId)}/`, {
    method: 'POST',
    body: JSON.stringify(body),
    token,
  })
}

export async function updateTeamMapPin(
  teamId: string,
  pinId: string,
  body: Partial<{
    name: string | null
    type: string | null
    latitude: number
    longitude: number
    images: string[]
    customSymbol: string
    color: number
    /** Omitted leaves the note alone; an empty string clears it. */
    description: string
  }>,
  token: string,
): Promise<TeamMapPinDto> {
  return apiFetch<TeamMapPinDto>(`${base(teamId)}/${encodeURIComponent(pinId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
    token,
  })
}

/**
 * Duplicates pictures of `sourcePinId` (a pin on another team's map) as new
 * stored objects for a pin being copied into `teamId`, and returns the new ids
 * in the same order. Done by the API because the browser cannot read the
 * picture bytes back out of the bucket.
 */
export async function copyTeamMapPinImages(
  teamId: string,
  sourcePinId: string,
  imageIds: string[],
  token: string,
): Promise<string[]> {
  const r = await apiFetch<{ images: string[] }>(`${base(teamId)}/copy-images`, {
    method: 'POST',
    body: JSON.stringify({ sourcePinId, imageIds }),
    token,
  })
  return r.images
}

export async function deleteTeamMapPin(teamId: string, pinId: string, token: string): Promise<void> {
  await apiFetch<undefined>(`${base(teamId)}/${encodeURIComponent(pinId)}`, {
    method: 'DELETE',
    token,
  })
}

/**
 * Files a team pin into one of the team's folders, or takes it out of any folder
 * when `folderId` is null. Separate from {@link updateTeamMapPin} because that
 * body's nulls mean "leave unchanged", so it cannot express unfiling — and
 * because filing a pin writes only that one field, leaving everything the app
 * owns on the document alone.
 */
export async function fileTeamMapPin(
  teamId: string,
  pinId: string,
  folderId: string | null,
  token: string,
): Promise<void> {
  await apiFetch<undefined>(`${base(teamId)}/${encodeURIComponent(pinId)}/folder`, {
    method: 'PUT',
    body: JSON.stringify({ folderId }),
    token,
  })
}
